import { Button } from "../../../Components/UI/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../Components/UI/card";
import { useCurrency } from "../../../Hooks/useCurrency";
import { Award, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { checkUserRights, FormatAmount, formatAmount, safeParseJsonArray } from "../../../Utils/Utilities";
import { getAgencyData, getMasterDataFromConfig } from "../../../Utils/Storage";
import useIsMobile from "../../../Hooks/useIsMobile";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import MastersDialog from "../../Settings/Masters";

const ProductLeaderboard = () => {
  const navigate = useNavigate();
  const currencySymbol = useCurrency();
  const agencyData = getAgencyData();
  const isMobile = useIsMobile();
  const { data: activeData } = useSelector(state => state.activeData);
  const products = useSelector(state => state.masterData?.products || []);

  const [showAll, setShowAll] = useState(false);
  const [maximumDecimal, setMaximumDecimal] = useState(0);
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);

  // Compact formatter for mobile view spacing
  const formatShortAmount = (amt, countryID = 1) => {
    if (isNaN(amt) || amt < 0) return "0";
    
    // Indian Format
    if (Number(countryID) === 1) {
      if (amt < 10000) {
        return Math.round(amt).toLocaleString("en-US");
      } else if (amt < 1000000) {
        return Math.round(amt / 1000) + " K";
      } else if (amt < 100000000) {
        return Math.round(amt / 100000) + " L";
      } else {
        return Math.round(amt / 10000000) + " Cr";
      }
    }
    // International Format
    else {
      if (amt < 10000) {
        return Math.round(amt).toLocaleString("en-US");
      } else if (amt < 1000000) {
        return Math.round(amt / 1000) + " K";
      } else if (amt < 1000000000) {
        return Math.round(amt / 1000000) + " M";
      } else {
        return Math.round(amt / 1000000000) + " B";
      }
    }
  };

  // 1. Parse Monthly Product Stats with fallback to Dummy JSON
  const leaderboardData = useMemo(() => {
    const nowDT = new Date();
    const currMonthNumber = nowDT.getMonth() + 1;
    const allProducts = getMasterDataFromConfig('Products') || [];

    try {
      const monthlyData = JSON.parse(activeData?.MonthlyThreads || "[]");
      const salesOrders = monthlyData.filter(m => m.TID === 2 && m.MM === currMonthNumber);
      if (salesOrders.length > 0) {
        const lastOrder = salesOrders[salesOrders.length - 1];
        if (lastOrder.Prds) {
          const parsedPrds = safeParseJsonArray(lastOrder.Prds);
          if (parsedPrds.length > 0) {
            // If backend data is active, parse and sort descending
            return parsedPrds
              .map((item) => {
                const foundProd = allProducts.find(p => String(p.id || p.PID) === String(item.PID));
                return {
                  id: item.PID,
                  name: foundProd ? (foundProd.name || foundProd.PNm) : `Archived Product (${item.PID})`,
                  price: item.RA || 0,
                  orders: item.RC || 0
                };
              })
              .sort((a, b) => b.price - a.price);
          }
        }
      }
    } catch (e) {
      console.error("Error reading MonthlyThreads products stats:", e);
    }

    // Fallback: If no stats for this month, but products have been entered by the user
    if (allProducts.length > 0) {
      return allProducts.map(p => ({
        id: p.id || p.PID,
        name: p.name || p.PNm,
        price: 0,
        orders: 0
      }));
    }

    // Fallback if no backend data exists and no products are entered
    return [];
  }, [activeData, products]);

  const mobileDisplayedData = leaderboardData.slice(0, 5);

  const getDecimalPlaces = (num) => {
    if (num == null) return 0;
    const str = num.toString();
    return str.includes('.') ? str.split('.')[1].length : 0;
  };

  useEffect(() => {
    if (!Array.isArray(leaderboardData) || leaderboardData.length === 0) {
      setMaximumDecimal(0);
      return;
    }

    const maxDecimals = leaderboardData.reduce((max, item) => {
      const decimals = getDecimalPlaces(item.price);
      return decimals > max ? decimals : max;
    }, 0);

    setMaximumDecimal(maxDecimals);
  }, [leaderboardData]);

  // 2. Calculate Total aggregated values for product leaderboard from Thread Type 2 stats
  const totalStats = useMemo(() => {
    const nowDT = new Date();
    const currMonthNumber = nowDT.getMonth() + 1;
    const currYear = nowDT.getFullYear();
    let orderCounts = 0;
    let orderValues = 0;

    try {
      const monthlyData = JSON.parse(activeData?.MonthlyThreads || "[]");
      const matchedRow = monthlyData.find(
        m => m.TID === 2 && m.MM === currMonthNumber && m.YY === currYear
      );
      if (matchedRow) {
        orderValues = matchedRow.Amt || 0;
        orderCounts = matchedRow.Rcvd || 0;
      }
    } catch (e) {
      console.error("Error reading MonthlyThreads for totalStats:", e);
    }
    return { orderCounts, orderValues };
  }, [activeData]);

  const currentMonth = new Date().getMonth() + 1;

  const handleProductClick = (product) => {
    navigate(`/threads-report?tID=2&m=${currentMonth}&pID=${product.id}`);
  };

  const allValuesAreZero = leaderboardData.length === 0 || leaderboardData.every(
    item => !item.price && !item.orders
  );

  if (allValuesAreZero) return null;

  return (
    <>
      <Card
        className={`flex flex-col overflow-hidden bg-sahi-white md:shadow-md ${isMobile ? '!pt-0' : '!pt-3'} !rounded-xl border-none shadow-sm gap-0 md:gap-3 cursor-pointer hover:border-slate-300 transition-all`}
        onClick={() => navigate(`/threads-report?tID=2&m=${currentMonth}`)}
      >
        {/* Desktop Header */}
        <CardHeader className="sm:pt-0 pt-3 px-5 flex-shrink-0 hidden md:block">
          <CardTitle className="flex items-center gap-2 text-lg justify-between">
            <div className="flex items-center">
              <span className="text-text-color text-lg sm:text-xl font-semibold">Product Performance</span>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="text-right flex items-baseline gap-1">
                <div className="text-xl font-bold text-sahi-dark-blue flex items-baseline gap-1">
                  <span className="text-lg text-sahi-grey">{currencySymbol}</span>
                  {totalStats.orderValues > 0 ? (
                    <FormatAmount amount={totalStats.orderValues} countryID={agencyData?.CountryID || 1} showCurr={false}/>
                  ) : (
                    <span>0</span>
                  )}
                  <span className="text-base font-semibold text-sahi-grey">
                    ({totalStats.orderCounts.toLocaleString()})
                  </span>
                </div>
              </div>
            </div>
          </CardTitle>
        </CardHeader>

        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-3 px-3 pb-1">
          <div className="flex items-center">
            <span className="text-text-color text-lg sm:text-xl font-semibold sahi-radius">
              Product Performance
            </span>
          </div>

          <div className="flex items-center ml-auto">
            <div className="text-lg font-bold text-sahi-dark-blue flex items-baseline gap-1">
              <span className="text-base text-sahi-grey">{currencySymbol}</span>
              {totalStats.orderValues > 0 ? (
                <span>{formatShortAmount(totalStats.orderValues, agencyData?.CountryID || 1)}</span>
              ) : (
                <span>0</span>
              )}
              <span className="text-sm font-semibold text-sahi-grey">
                ({totalStats.orderCounts.toLocaleString()})
              </span>
            </div>
          </div>
        </div>

        <CardContent className="md:px-5 md:pb-3 pb-3 px-3 m-0">        
          <div className="space-y-2 md:flex-1">
            {leaderboardData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                <p className="text-sm text-gray-500 mb-4">No offerings added yet. Add your first offering to start tracking performance.</p>
                <Button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddProductDialog(true);
                  }}
                  className="sahi-primary-button text-sm flex items-center gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Add Offering
                </Button>
              </div>
            ) : (
              <>
                {/* Desktop View */}
                <div className="hidden md:block">
                  {leaderboardData.slice(0, 5).map((product, index) => (
                    <div
                      key={product.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(product);
                      }}
                      className="flex items-center gap-2 md:p-2 md:px-3 p-1 px-3 rounded-lg border border-sahi-border-color md:mx-0 bg-sahi-disable mb-2 shadow-sm hover:cursor-pointer"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-4 h-4 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-sm bg-sahi-primary-color text-sahi-white">
                          {index + 1}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div
                          className="flex items-center font-semibold text-sm sm:text-base leading-tight"
                          style={{
                            color: index === 0
                              ? "var(--sahi-gold)"
                              : index === 1
                                ? "var(--sahi-silver)"
                                : index === 2
                                  ? "var(--sahi-bronze)"
                                  : "var(--sahi-grey)"
                          }}
                        >
                          <span className="text-foreground truncate">
                            {product.name}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-sm sm:text-base leading-tight text-sahi-primary-color flex items-baseline gap-1">
                          <span>
                            {!isMobile && product.price !== 0 ? (
                              <FormatAmount amount={product.price} countryID={agencyData?.CountryID || 1} prefix={currencySymbol} showDecimalPoints={maximumDecimal} />
                            ) : (
                              <span>{currencySymbol}{formatAmount(product.price, agencyData?.CountryID || 1)}</span>
                            )}
                          </span>
                          <span className="text-sm opacity-80 font-medium">({product.orders})</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile View */}
                <div className="md:hidden space-y-2">
                  {mobileDisplayedData.map((product, index) => (
                    <div
                      key={product.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(product);
                      }}
                      className="flex items-center gap-2 md:p-3 md:px-3 p-2 px-3 sahi-radius border border-sahi-border-color md:mx-0 bg-sahi-disable shadow-sm hover:cursor-pointer"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-sm bg-sahi-primary-color text-sahi-white">
                          {index + 1}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div
                          className="flex items-center font-semibold text-sm sm:text-base leading-tight"
                          style={{
                            color: index === 0
                              ? "var(--sahi-gold)"
                              : index === 1
                                ? "var(--sahi-silver)"
                                : index === 2
                                  ? "var(--sahi-bronze)"
                                  : "var(--sahi-grey)"
                          }}
                        >
                          <span className="text-foreground truncate">
                            {product.name}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-sm sm:text-lg leading-tight mb-1 text-sahi-primary-color flex items-baseline gap-1">
                          <span>{currencySymbol} {formatShortAmount(product.price, agencyData?.CountryID || 1)}</span>
                          <span className="text-xs font-semibold text-sahi-dark-blue">({product.orders})</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Capped at 5 items, no show more toggle needed */}
        </CardContent>
      </Card>

      <MastersDialog
        open={showAddProductDialog}
        onClose={() => setShowAddProductDialog(false)}
        masterType="products"
        directAdd={true}
        openEntry={true}
      />
    </>
  );
};

export default ProductLeaderboard;
